import { useEffect, useMemo, useState } from 'react';
import {
  lastReportedAt,
  liveWindows,
  usageCapacity,
  USAGE_FULL_AT,
  type Agent,
  type Subscription,
  type Task,
  type UsageCapacity,
  type UsageWindow,
} from '@shared/domain.ts';
import { useRigg } from '@/data/RiggProvider.tsx';
import { api } from '@/lib/api.ts';
import { ago, clock, cn, duration } from '@/lib/format.ts';
import { navigate } from '@/lib/router.ts';
import { Button, Checkbox, Dialog, Field, Input, Tip, useToast } from './ui.tsx';

/**
 * How much room each Claude subscription has, and whether the queue behind it
 * is moving — the two things that decide where the next task goes, shown so
 * that a person can check the decision rather than take it on trust.
 *
 * Status is always a glyph and a word as well as a colour, and a meter is
 * drawn only when a routine reported a real figure. The session record usually
 * carries none, and a bar invented from "allowed" would be a number nobody
 * measured.
 */

type Tier = 0 | 1 | 2;

const TONE: Record<Tier, { glyph: string; text: string; fill: string; track: string }> = {
  2: { glyph: '●', text: 'text-goodx', fill: 'bg-goodx', track: 'bg-goodx/15' },
  1: { glyph: '▲', text: 'text-amberx', fill: 'bg-amberx', track: 'bg-amberx/15' },
  0: { glyph: '✕', text: 'text-warnx', fill: 'bg-warnx', track: 'bg-warnx/15' },
};

const WINDOW_LABEL: Record<string, string> = {
  five_hour: '5-hour',
  seven_day: 'Weekly',
  seven_day_opus: 'Weekly · Opus',
  seven_day_sonnet: 'Weekly · Sonnet',
  seven_day_overage_included: 'Weekly',
};

export function windowLabel(window: string): string {
  return WINDOW_LABEL[window] ?? window.replace(/_/g, ' ');
}

function isFull(reading: UsageWindow): boolean {
  return reading.utilization !== null && reading.utilization >= USAGE_FULL_AT && !reading.overage;
}

function windowTier(reading: UsageWindow): Tier {
  if (reading.status === 'rejected' || isFull(reading)) return 0;
  if (reading.status === 'allowed_warning' || reading.overage) return 1;
  return 2;
}

function windowWord(reading: UsageWindow): string {
  if (reading.status === 'rejected') return 'limit reached';
  if (isFull(reading)) return 'full — not sent work';
  if (reading.status === 'allowed_warning') return 'about to run out';
  if (reading.overage) return 'on extra usage';
  return 'has room';
}

/** "14:40" today, "Sun 13:00" further out. */
function resetsAt(iso: string, now: number): string {
  if (Date.parse(iso) - now < 20 * 3600_000) return clock(iso);
  return `${new Date(iso).toLocaleDateString(undefined, { weekday: 'short' })} ${clock(iso)}`;
}

/** "14:40" today, "in 3d 4h" further out. */
function resetsIn(iso: string, now: number): string {
  const ms = Date.parse(iso) - now;
  return ms < 20 * 3600_000 ? `resets ${clock(iso)}` : `resets in ${duration(ms)}`;
}

export function CapacityBadge({ capacity, now }: { capacity: UsageCapacity; now: number }) {
  const tone = TONE[capacity.tier];
  const word =
    capacity.tier === 0
      ? capacity.back_at && Date.parse(capacity.back_at) > now
        ? `full until ${resetsAt(capacity.back_at, now)}`
        : 'full'
      : capacity.tier === 1
        ? 'near limit'
        : 'has room';
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1.5 text-[12px] font-semibold', tone.text)}>
      <span aria-hidden className="text-[10px] leading-none">{tone.glyph}</span>
      {word}
    </span>
  );
}

function WindowRow({ reading, now }: { reading: UsageWindow; now: number }) {
  const tier = windowTier(reading);
  const tone = TONE[tier];
  const percent = reading.utilization !== null ? Math.round(reading.utilization * 100) : null;

  return (
    <div className="flex flex-col gap-1.5 border-t border-line py-2.5 first:border-t-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="w-28 shrink-0 text-[13px] font-semibold text-t2">{windowLabel(reading.window)}</span>
        <span className={cn('inline-flex items-center gap-1.5 text-[12.5px]', tone.text)}>
          <span aria-hidden className="text-[10px] leading-none">{tone.glyph}</span>
          {windowWord(reading)}
          {percent !== null ? <span className="text-t2 tabular-nums">· {percent}% used</span> : null}
        </span>
        <span className="ml-auto text-[12px] text-mute tabular-nums">
          {reading.resets_at ? resetsIn(reading.resets_at, now) : 'no reset time'}
        </span>
      </div>
      {percent !== null ? (
        <div
          role="meter"
          aria-label={`${windowLabel(reading.window)} usage`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className={cn('h-1.5 w-full overflow-hidden rounded-full', tone.track)}
        >
          <div className={cn('h-full rounded-full', tone.fill)} style={{ width: `${Math.max(2, percent)}%` }} />
        </div>
      ) : null}
      <div className="text-[11.5px] text-faint">
        {reading.reported_by === 'Rigg'
          ? `checked ${ago(reading.reported_at, now)}`
          : `reported ${ago(reading.reported_at, now)}${reading.reported_by ? ` by ${reading.reported_by}` : ''}`}
      </div>
    </div>
  );
}

/** One card per subscription: its windows, its routines, its key, and who is next. */
export function SubscriptionsPanel({
  projectId,
  subscriptions,
  agents,
  nextUp,
  isAdmin,
  now,
}: {
  projectId: string;
  subscriptions: Subscription[];
  /** The project's routines, design workers left out. */
  agents: Agent[];
  /** Ids of the routines auto-pick would send the next task to. */
  nextUp: Set<string>;
  isAdmin: boolean;
  now: number;
}) {
  const { notify } = useToast();
  const { projects, adminProjectIds } = useRigg();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const projectName = (id: string) => projects.find((p) => p.id === id)?.name ?? 'another project';

  const sorted = useMemo(() => [...subscriptions].sort((a, b) => a.name.localeCompare(b.name)), [subscriptions]);
  const unlinked = agents.filter((a) => !a.subscription_id || !subscriptions.some((s) => s.id === a.subscription_id));

  const [refreshing, setRefreshing] = useState(false);
  const checkable = sorted.filter((s) => s.token_hint);

  // Every keyed subscription at once, rather than one "Check now" at a time —
  // the readings come back through Realtime like any other row change.
  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const results = await Promise.allSettled(checkable.map((s) => api.checkSubscription(s.id)));
      const failed = results.flatMap((result, i) =>
        result.status === 'rejected'
          ? [`${checkable[i]!.name}: ${(result.reason as Error).message}`]
          : result.value.subscription.last_poll_error
            ? [`${result.value.subscription.name}: ${result.value.subscription.last_poll_error}`]
            : [],
      );
      if (failed.length) notify(`Could not check ${failed.length} of ${checkable.length}`, { body: failed.join('; '), tone: 'bad' });
      else notify(checkable.length === 1 ? 'Subscription checked' : `${checkable.length} subscriptions checked`);
    } finally {
      setRefreshing(false);
    }
  };

  const check = async (subscription: Subscription) => {
    setChecking(subscription.id);
    try {
      const { subscription: checked } = await api.checkSubscription(subscription.id);
      if (checked.last_poll_error) notify(`Could not check ${checked.name}`, { body: checked.last_poll_error, tone: 'bad' });
      else notify(`${checked.name} checked`);
    } catch (error) {
      notify(`Could not check ${subscription.name}`, { body: (error as Error).message, tone: 'bad' });
    } finally {
      setChecking(null);
    }
  };

  return (
    <section className="mb-6">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="m-0 text-[15px] font-[650] text-t1">Subscriptions</h2>
        {isAdmin ? (
          <span className="ml-auto flex gap-2">
            {checkable.length > 0 ? (
              <Tip content="Check every subscription with a key now, rather than waiting for the hourly check.">
                <Button size="sm" disabled={refreshing || checking !== null} onClick={() => void refreshAll()}>
                  {refreshing ? 'Refreshing…' : 'Refresh usage'}
                </Button>
              </Tip>
            ) : null}
            <Button size="sm" onClick={() => setEditing('new')}>
              Add subscription
            </Button>
          </span>
        ) : null}
      </div>
      <p className="mt-1 mb-3 max-w-2xl text-[12.5px] leading-relaxed text-mute">
        The Claude accounts your routines run on. Give one a key and Rigg checks its limits every hour;
        the next task goes to the routine whose subscription has the most room. Share one with your other
        projects and they all read the same key and the same usage.
      </p>

      {sorted.length === 0 ? (
        <p className="m-0 rounded-xl border border-dashed border-line px-4 py-3 text-[12.5px] text-mute">
          No subscriptions yet. Add one per Claude account your routines run on, then pick it under each
          routine&rsquo;s Edit.
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {sorted.map((subscription) => {
            const members = agents.filter((a) => a.subscription_id === subscription.id);
            const windows = liveWindows(subscription.usage, now);
            const last = lastReportedAt(subscription.usage);
            const canManage = adminProjectIds.includes(subscription.project_id);
            const sharedFrom = subscription.project_id !== projectId ? subscription.project_id : null;
            const alsoIn = [subscription.project_id, ...(subscription.shared_project_ids ?? [])].filter(
              (id) => id !== projectId,
            );
            return (
              <div key={subscription.id} className="flex flex-col rounded-xl border border-line bg-panel px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-t1">{subscription.name}</span>
                  {last ? <CapacityBadge capacity={usageCapacity({ usage: subscription.usage }, now)} now={now} /> : null}
                </div>
                {alsoIn.length > 0 ? (
                  <Tip content="One subscription, one key, one usage reading — routines in every project it is in count against the same limits.">
                    <div className="mt-0.5 truncate text-[12px] text-faint">
                      {sharedFrom ? `Shared from ${projectName(sharedFrom)}` : 'Shared'}
                      {alsoIn.filter((id) => id !== sharedFrom).length > 0
                        ? ` · also used by ${alsoIn.filter((id) => id !== sharedFrom).map(projectName).join(', ')}`
                        : ''}
                    </div>
                  </Tip>
                ) : null}
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-[12px] text-mute">
                  {members.length === 0 ? (
                    <span>No routines on it — pick it under a routine&rsquo;s Edit.</span>
                  ) : (
                    members.map((agent, i) => (
                      <span key={agent.id} className={nextUp.has(agent.id) ? 'text-accent' : undefined}>
                        {agent.name}
                        {nextUp.has(agent.id) ? ' (next up)' : ''}
                        {i < members.length - 1 ? <span className="text-faint">,</span> : null}
                      </span>
                    ))
                  )}
                </div>

                <div className="mt-2 flex-1">
                  {windows.length > 0 ? (
                    windows.map((reading) => <WindowRow key={reading.window} reading={reading} now={now} />)
                  ) : (
                    <p className="m-0 border-t border-line pt-2.5 text-[12.5px] text-mute">
                      {last
                        ? `Last reading ${ago(last, now)}, and its window has reset since — treated as having room.`
                        : subscription.last_poll_error
                          ? `The check failed: ${subscription.last_poll_error}.`
                          : subscription.token_hint
                            ? 'Not checked yet.'
                            : 'No key, so nothing to check — treated as having room.'}
                    </p>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-line pt-2.5 text-[12px]">
                  {subscription.token_hint ? (
                    subscription.last_poll_error ? (
                      <Tip content={subscription.last_poll_error}>
                        <span className="inline-flex min-w-0 items-center gap-1.5 text-warnx">
                          <span aria-hidden className="text-[10px]">✕</span>
                          <span className="truncate">check failed {subscription.last_polled_at ? ago(subscription.last_polled_at, now) : ''}</span>
                        </span>
                      </Tip>
                    ) : (
                      <span className="text-mute">
                        <span className="font-mono text-[11.5px]">{subscription.token_hint}</span>
                        {subscription.last_polled_at ? ` · checked ${ago(subscription.last_polled_at, now)}` : ''}
                      </span>
                    )
                  ) : (
                    <span className="text-faint">no key</span>
                  )}
                  {isAdmin ? (
                    <span className="ml-auto flex gap-2">
                      {subscription.token_hint ? (
                        <Button size="sm" disabled={refreshing || checking === subscription.id} onClick={() => void check(subscription)}>
                          {checking === subscription.id ? 'Checking…' : 'Check now'}
                        </Button>
                      ) : null}
                      {canManage ? (
                        <Button size="sm" onClick={() => setEditing(subscription.id)}>
                          {subscription.token_hint ? 'Edit' : 'Add key'}
                        </Button>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {unlinked.length > 0 ? (
        <p className="mt-2 mb-0 text-[12px] text-mute">
          Not on a subscription: {unlinked.map((a) => a.name).join(', ')} — treated as having room, each on its own.
        </p>
      ) : null}

      {editing ? (
        <SubscriptionDialog
          projectId={projectId}
          subscription={editing === 'new' ? null : (subscriptions.find((s) => s.id === editing) ?? null)}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}

function SubscriptionDialog({
  projectId,
  subscription,
  onClose,
}: {
  projectId: string;
  subscription: Subscription | null;
  onClose: () => void;
}) {
  const { notify } = useToast();
  const { projects, adminProjectIds } = useRigg();
  const [name, setName] = useState(subscription?.name ?? '');
  const [token, setToken] = useState('');
  const [shared, setShared] = useState<string[]>(subscription?.shared_project_ids ?? []);
  const [busy, setBusy] = useState(false);

  // Only projects I administer can be added or taken off here; any other
  // project already on the list stays on it, untouched.
  const owner = subscription?.project_id ?? projectId;
  const shareable = projects.filter((p) => p.id !== owner && adminProjectIds.includes(p.id));
  const sharedChanged =
    shared.length !== (subscription?.shared_project_ids ?? []).length ||
    shared.some((id) => !(subscription?.shared_project_ids ?? []).includes(id));

  const run = async (what: string, action: () => Promise<{ subscription?: Subscription } | unknown>) => {
    setBusy(true);
    try {
      const result = (await action()) as { subscription?: Subscription } | undefined;
      const saved = result?.subscription;
      if (saved?.last_poll_error) {
        notify(`Saved, but the check failed`, { body: saved.last_poll_error, tone: 'bad' });
      } else if (saved && token.trim()) {
        notify(`${saved.name} checked`, { body: 'The key works — its limits are on the card.' });
      }
      onClose();
    } catch (error) {
      notify(`Could not ${what}`, { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run('save', () =>
      subscription
        ? api.updateSubscription(subscription.id, {
            name: name.trim(),
            ...(token.trim() ? { token: token.trim() } : {}),
            ...(sharedChanged ? { shared_project_ids: shared } : {}),
          })
        : api.addSubscription({
            project_id: projectId,
            name: name.trim(),
            token: token.trim() || null,
            shared_project_ids: shared,
          }),
    );

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={subscription ? `Edit ${subscription.name}` : 'Add a subscription'}
      description="One per Claude account your routines run on. Routines on the same subscription share its limits."
      footer={
        <>
          {subscription ? (
            <Button
              tone="danger"
              className="mr-auto"
              disabled={busy}
              onClick={() => void run('remove it', () => api.removeSubscription(subscription.id))}
            >
              Remove
            </Button>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button tone="primary" disabled={busy || !name.trim()} onClick={() => void save()}>
            {busy ? 'Saving…' : subscription ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <Field label="Name" hint="Anything that tells the accounts apart — the browser profile it is signed in to works.">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Safari" autoFocus maxLength={80} />
      </Field>
      <Field
        label="Key"
        hint={
          <>
            Run <code>claude setup-token</code> while signed in to this account and paste what it prints (it starts{' '}
            <code>sk-ant-oat</code>). Rigg makes one one-token Haiku call an hour with it and reads the limits off the
            answer. It is stored where no browser can read it.
            {subscription?.token_hint ? ' Leave blank to keep the current one.' : ''}
          </>
        }
      >
        <Input
          value={token}
          onChange={(e) => setToken(e.target.value)}
          type="password"
          autoComplete="off"
          placeholder={subscription?.token_hint ?? 'sk-ant-oat01-…'}
        />
      </Field>
      {shareable.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-[11.5px] font-semibold text-mute">Also used by</span>
          {shareable.map((p) => (
            <Checkbox
              key={p.id}
              id={`share-${p.id}`}
              checked={shared.includes(p.id)}
              onChange={(on) => setShared((ids) => (on ? [...ids, p.id] : ids.filter((id) => id !== p.id)))}
              label={p.name}
            />
          ))}
          <span className="text-[12px] leading-relaxed text-mute">
            Routines in a project ticked here can run on this subscription, and every project reads the same key
            and the same usage. Unticking one takes its routines off it.
          </span>
        </div>
      ) : null}
      {subscription?.token_hint ? (
        <button
          type="button"
          className="self-start text-[12px] text-mute underline"
          disabled={busy}
          onClick={() => void run('remove the key', () => api.updateSubscription(subscription.id, { token: null }))}
        >
          Remove the key
        </button>
      ) : null}
    </Dialog>
  );
}

interface QueueJob {
  jobname: string;
  active: boolean;
  last_run: string | null;
  last_status: string | null;
  last_response: number | null;
  last_error: string | null;
}

/**
 * The tasks waiting for a routine, and whether the job that retries them is
 * actually running — without the second half the first is a list that might
 * never move.
 */
export function QueuePanel({
  projectId,
  tasks,
  now,
}: {
  projectId: string;
  tasks: Task[];
  now: number;
}) {
  const [job, setJob] = useState<QueueJob | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    const load = () =>
      api
        .dispatchHealth(projectId)
        .then((result) => live && setJob(result.queue_job as QueueJob | null))
        .catch(() => live && setJob(undefined));
    void load();
    const timer = setInterval(load, 60_000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [projectId]);

  const queued = useMemo(
    () =>
      tasks
        .filter((t) => t.queued_at && !t.deleted_at && !t.archived_at)
        .sort((a, b) => a.queued_at!.localeCompare(b.queued_at!)),
    [tasks],
  );

  const jobOk =
    job && job.active && job.last_run && now - Date.parse(job.last_run) < 5 * 60_000 &&
    job.last_status !== 'failed' && (job.last_response === null || job.last_response < 400);

  return (
    <section className="mb-6 rounded-xl border border-line bg-panel px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="m-0 text-[14px] font-semibold text-t1">Dispatch queue</h2>
        <span className="text-[12.5px] text-body">
          {queued.length === 0 ? 'empty — every task has reached a routine' : `${queued.length} waiting`}
        </span>
        <span className="ml-auto text-[12px]">
          {job === undefined ? (
            <span className="text-mute">checking the queue job…</span>
          ) : job === null ? (
            <Tip content="The rigg-dispatch-queue cron job does not exist on this database, so nothing retries a queued task. It is created by the dispatch_queue migration when pg_cron is available.">
              <span className="inline-flex items-center gap-1.5 text-warnx">
                <span aria-hidden className="text-[10px]">✕</span>queue job not scheduled
              </span>
            </Tip>
          ) : jobOk ? (
            <span className="inline-flex items-center gap-1.5 text-goodx">
              <span aria-hidden className="text-[10px]">●</span>checked {ago(job.last_run, now)}
            </span>
          ) : (
            <Tip content={job.last_error ?? 'The job has not run in the last five minutes.'}>
              <span className="inline-flex items-center gap-1.5 text-warnx">
                <span aria-hidden className="text-[10px]">✕</span>
                {!job.active
                  ? 'queue job paused'
                  : !job.last_run
                    ? 'queue job has never run'
                    : job.last_response && job.last_response >= 400
                      ? `last run answered HTTP ${job.last_response}`
                      : `last ran ${ago(job.last_run, now)}`}
              </span>
            </Tip>
          )}
        </span>
      </div>

      {queued.length > 0 ? (
        <ul className="m-0 mt-2 list-none p-0">
          {queued.slice(0, 6).map((task) => (
            <li key={task.id} className="flex flex-wrap items-baseline gap-x-2 border-t border-line py-1.5 text-[12.5px]">
              <a
                href={`/t/${task.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/t/${task.id}`);
                }}
                className="min-w-0 max-w-[55%] truncate font-semibold"
              >
                #{task.ref} {task.title}
              </a>
              <span className="min-w-0 flex-1 truncate text-mute">
                {task.queue_reason ?? 'waiting for a routine'}
                {task.queue_until && Date.parse(task.queue_until) > now ? ` · first back ${clock(task.queue_until)}` : ''}
              </span>
              <span className="shrink-0 text-[11.5px] text-faint tabular-nums">
                waiting {ago(task.queued_at, now).replace(' ago', '')}
              </span>
            </li>
          ))}
          {queued.length > 6 ? (
            <li className="border-t border-line pt-1.5 text-[12px] text-mute">and {queued.length - 6} more</li>
          ) : null}
        </ul>
      ) : null}
    </section>
  );
}
